import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import BusinessPage from './pages/Business';
import CustomersPage from './pages/WhyRegister';
import ContactPage from './pages/Contact';
import Dashboard from './pages/Dashboard';
import AboutUs from './pages/AboutUs';
import HelpCenter from './pages/HelpCenter';
import FAQs from './pages/FAQs';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Footer from './components/Footer';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#e3662d', // Vibrant Orange from logo
      light: '#f17a4a',
      dark: '#d55a1f',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#5a9b9a', // Muted Teal accent
      light: '#7ab3b2',
      dark: '#4a8a89',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#f5f5f5', // Light Off-White from logo
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2d2d2d', // Dark Gray from logo
      secondary: '#5a5a5a',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    success: {
      main: '#5a9b9a', // Using muted teal for success
      light: '#7ab3b2',
      dark: '#4a8a89',
    },
    warning: {
      main: '#e6b8a2', // Dusty Rose for warning
      light: '#f0c9b8',
      dark: '#d4a08c',
    },
    info: {
      main: '#5a9b9a', // Using muted teal for info
      light: '#7ab3b2',
      dark: '#4a8a89',
    },
    // Custom colors for the expanded palette
    custom: {
      dustyRose: '#e6b8a2',
      lightOffWhite: '#f5f5f5',
      darkGray: '#2d2d2d',
      vibrantOrange: '#e3662d',
      mutedTeal: '#5a9b9a',
    },
  },
  typography: {
    fontFamily: "'Inter', 'Manrope', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      color: '#2d2d2d',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      color: '#2d2d2d',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      color: '#2d2d2d',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      color: '#2d2d2d',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      color: '#2d2d2d',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: '#2d2d2d',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#2d2d2d',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#5a5a5a',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 20, // More rounded to mimic the circular logo
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 25, // Pill-shaped buttons like the logo
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0px 6px 20px rgba(227, 102, 45, 0.25)',
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: '0px 4px 15px rgba(227, 102, 45, 0.2)',
          '&:hover': {
            boxShadow: '0px 8px 25px rgba(227, 102, 45, 0.3)',
          },
        },
        outlined: {
          borderColor: '#e3662d',
          color: '#e3662d',
          '&:hover': {
            backgroundColor: 'rgba(227, 102, 45, 0.08)',
            borderColor: '#d55a1f',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24, // More rounded corners
          boxShadow: '0px 4px 20px rgba(45, 45, 45, 0.08)',
          border: '1px solid #eeeeee',
          backgroundColor: '#FFFFFF',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0px 8px 30px rgba(45, 45, 45, 0.15)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16, // More rounded chips
          fontWeight: 600,
          backgroundColor: '#f5f5f5',
          color: '#2d2d2d',
          '&:hover': {
            backgroundColor: '#e6b8a2',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 24, // More rounded papers
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            backgroundColor: '#FFFFFF',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e6b8a2',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e3662d',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0px 2px 20px rgba(45, 45, 45, 0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="App">
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/business" element={<BusinessPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/app" element={<Dashboard />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
